import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronDown, Mail, Menu, Phone } from "lucide-react";
import { useContent } from "@/content/ContentContext";
import { LangSwitcher, useLang } from "@/i18n/LanguageContext";
import MenuOverlay from "@/components/landing/MenuOverlay";

const navCls = ({ isActive }) =>
  `text-xs uppercase tracking-[0.14em] transition-colors duration-300 ${
    isActive ? "text-harvest" : "text-sage hover:text-cream"
  }`;

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const content = useContent();
  const { t } = useLang();
  const c = content.contact;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 border-b border-line bg-forest/85 backdrop-blur-2xl transition-shadow duration-500 ${
          scrolled ? "shadow-lg shadow-[#0B1A33]/5" : ""
        }`}
      >
        <div
          className={`hidden overflow-hidden border-b border-line transition-all duration-500 md:block ${
            scrolled ? "max-h-0 border-transparent opacity-0" : "max-h-12 opacity-100"
          }`}
        >
          <div className="mx-auto flex w-full max-w-[1440px] items-center justify-between px-6 py-2.5 lg:px-12">
            <div className="flex items-center gap-6">
              <a
                href={`tel:${c.phone.replace(/\s/g, "")}`}
                data-testid="topbar-phone-link"
                className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] text-sage transition-colors duration-300 hover:text-harvest"
              >
                <Phone className="h-3 w-3" /> {c.phone}
              </a>
              <a
                href={`mailto:${c.email}`}
                data-testid="topbar-email-link"
                className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] text-sage transition-colors duration-300 hover:text-harvest"
              >
                <Mail className="h-3 w-3" /> {c.email}
              </a>
            </div>
            <Link
              to="/contact?interest=dealership"
              data-testid="topbar-dealer-link"
              className="text-[11px] uppercase tracking-[0.15em] text-harvest transition-colors duration-300 hover:text-harvestHover"
            >
              {t("becomeDealer")}
            </Link>
          </div>
        </div>

        <div className="mx-auto flex w-full max-w-[1440px] items-center justify-between px-6 py-4 lg:px-12">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-3">
            <img
              src="/assets/jamna-logo.png"
              alt="Jamna Industries logo"
              className="h-10 w-auto"
            />
            <span className="hidden font-display text-xl font-bold tracking-tight text-cream sm:block">
              JAMNA <span className="italic text-harvest">INDUSTRIES</span>
            </span>
          </Link>

          <nav data-testid="main-nav" className="hidden items-center gap-6 lg:flex">
            <div className="group relative">
              <NavLink
                to="/products"
                data-testid="nav-link-products"
                className={(args) => `${navCls(args)} flex items-center gap-1 py-2`}
              >
                {t("navProducts")}
                <ChevronDown className="h-3 w-3 transition-transform duration-300 group-hover:rotate-180" />
              </NavLink>
              <div className="invisible absolute left-1/2 top-full z-50 w-[560px] -translate-x-1/2 translate-y-2 pt-4 opacity-0 transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                <div
                  data-testid="nav-dropdown-products"
                  className="grid max-h-[70vh] grid-cols-2 gap-x-6 overflow-y-auto border border-line bg-moss p-6 shadow-2xl shadow-[#0B1A33]/10"
                >
                  {content.products.map((p) => (
                    <Link
                      key={p.id}
                      to={`/products/${p.id}`}
                      data-testid={`nav-dd-${p.id}`}
                      className="group/item border-b border-line/60 py-3"
                    >
                      <span className="block text-sm font-semibold text-cream transition-colors duration-300 group-hover/item:text-harvest">
                        {p.name}
                      </span>
                      <span className="mt-0.5 block text-[11px] text-sage">{p.hindi}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <div className="group relative">
              <NavLink
                to="/fittings"
                data-testid="nav-link-fittings"
                className={(args) => `${navCls(args)} flex items-center gap-1 py-2`}
              >
                {t("navFittings")}
                <ChevronDown className="h-3 w-3 transition-transform duration-300 group-hover:rotate-180" />
              </NavLink>
              <div className="invisible absolute left-1/2 top-full z-50 w-72 -translate-x-1/2 translate-y-2 pt-4 opacity-0 transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                <div
                  data-testid="nav-dropdown-fittings"
                  className="border border-line bg-moss p-4 shadow-2xl shadow-[#0B1A33]/10"
                >
                  {(content.fittings || []).map((f) => (
                    <Link
                      key={f.id}
                      to="/fittings"
                      data-testid={`nav-dd-${f.id}`}
                      className="block border-b border-line/60 px-2 py-2.5 text-sm font-medium text-cream transition-colors duration-300 last:border-b-0 hover:text-harvest"
                    >
                      {f.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <div className="group relative">
              <NavLink
                to="/about"
                data-testid="nav-link-whoweare"
                className={(args) => `${navCls(args)} flex items-center gap-1 py-2`}
              >
                {t("navWhoWeAre")}
                <ChevronDown className="h-3 w-3 transition-transform duration-300 group-hover:rotate-180" />
              </NavLink>
              <div className="invisible absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 translate-y-2 pt-4 opacity-0 transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                <div
                  data-testid="nav-dropdown-whoweare"
                  className="border border-line bg-moss p-4 shadow-2xl shadow-[#0B1A33]/10"
                >
                  <Link to="/about" data-testid="nav-dd-about" className="block border-b border-line/60 px-2 py-2.5 text-sm font-medium text-cream transition-colors duration-300 hover:text-harvest">
                    {t("navAbout")}
                  </Link>
                  <Link to="/history" data-testid="nav-dd-history" className="block border-b border-line/60 px-2 py-2.5 text-sm font-medium text-cream transition-colors duration-300 hover:text-harvest">
                    {t("menuHistory")}
                  </Link>
                  <Link to="/leadership" data-testid="nav-dd-leadership" className="block border-b border-line/60 px-2 py-2.5 text-sm font-medium text-cream transition-colors duration-300 hover:text-harvest">
                    {t("menuLeadership")}
                  </Link>
                  <Link to="/sustainability" data-testid="nav-dd-sustainability" className="block px-2 py-2.5 text-sm font-medium text-cream transition-colors duration-300 hover:text-harvest">
                    {t("menuSustainability")}
                  </Link>
                </div>
              </div>
            </div>

            <NavLink to="/segments" data-testid="nav-link-segments" className={navCls}>
              {t("navSegments")}
            </NavLink>
            <NavLink to="/stories" data-testid="nav-link-stories" className={navCls}>
              {t("navStories")}
            </NavLink>
            <NavLink to="/updates" data-testid="nav-link-updates" className={navCls}>
              {t("navUpdates")}
            </NavLink>
            <NavLink to="/contact" data-testid="nav-link-contact" className={navCls}>
              {t("navContact")}
            </NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <LangSwitcher />
            <Link
              to="/contact"
              data-testid="header-enquire-button"
              className="group hidden items-center gap-2 bg-harvest px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-harvestHover sm:inline-flex"
            >
              {t("enquire")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="menu-open-button"
              aria-label="Open menu"
              className="border border-line p-2.5 text-cream transition-colors duration-300 hover:border-harvest hover:text-harvest"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </motion.header>

      <MenuOverlay open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}
